<template>
  <div class="stepwise-date-picker">
    <!-- Year Picker - Step 1 -->
    <date-picker
        v-if="internalStep === 1"
        v-model="internalYearDob"
        :class="{'invalid': invalid}"
        input-class="custom-input"
        type="year"
        placeholder="mm/dd/yyyy"
        :clearable="true"
        @clear="handleClear"
        @change="handleYearSelect"
        :disabled-date="(date) => {
          const year = date.getFullYear();
          const currentYear = new Date().getFullYear();
          return date >= new Date() || year < (currentYear - 100);
        }"
        :open.sync="yearPickerOpen"
        value-type="format"
        format="YYYY"
        ref="yearPicker"
    >
      <template #input='{ props, events }'>
        <input
            v-bind='props'
            size=10
            maxlength=10
            v-on='events'
            @click="openYearPicker"
            @input="handleManualYearInput"
            onkeyup="this.value=this.value.replace(/^(\d\d)(\d)$/g,'$1/$2').replace(/^(\d\d\/\d\d)(\d+)$/g,'$1/$2').replace(/[^\d\/]/g,'')"
            :value="displayValue"
        />
      </template>
    </date-picker>

    <!-- Month Picker - Step 2 -->
    <date-picker
        v-if="internalStep === 2"
        v-model="internalMonthDob"
        input-class="custom-input"
        :class="{'invalid': invalid}"
        type="month"
        :clearable="true"
        @clear="handleClear"
        placeholder="mm/dd/yyyy"
        @change="handleMonthSelect"
        :disabled-date="(date) => {
          const year = date.getFullYear();
          const currentYear = new Date().getFullYear();
          return date >= new Date() || year < (currentYear - 100);
        }"
        :open.sync="monthPickerOpen"
        :default-value="internalYearDob"
        value-type="format"
        format="MM"
        ref="monthPicker"
    >
      <template #input='{ props, events }'>
        <input
            v-bind='props'
            size=10
            maxlength=10
            v-on='events'
            @click="openMonthPicker"
            @input="handleManualMonthInput"
            onkeyup="this.value=this.value.replace(/^(\d\d)(\d)$/g,'$1/$2').replace(/^(\d\d\/\d\d)(\d+)$/g,'$1/$2').replace(/[^\d\/]/g,'')"
            :value="displayValue"
        />
      </template>
    </date-picker>

    <!-- Day Picker - Step 3 -->
    <date-picker
        v-if="internalStep === 3"
        input-class="custom-input"
        placeholder="mm/dd/yyyy"
        type="date"
        :clearable="true"
        :editable="true"
        @change="handleDateSelect"
        @clear="handleClear"
        :disabled-date="(date) => {
          const year = date.getFullYear();
          const currentYear = new Date().getFullYear();
          return date >= new Date() || year < (currentYear - 100);
        }"
        v-model="internalDob"
        :class="{'invalid': invalid}"
        :open.sync="dayPickerOpen"
        :default-value="defaultDateValue"
        format="MM/DD/YYYY"
        value-type="format"
        ref="dayPicker"
    >
      <template #input='{ props, events }'>
        <input
            v-bind='props'
            size=10
            maxlength=10
            v-on='events'
            @input="handleManualDateInput"
            @click="openDayPicker"
            onkeyup="this.value=this.value.replace(/^(\d\d)(\d)$/g,'$1/$2').replace(/^(\d\d\/\d\d)(\d+)$/g,'$1/$2').replace(/[^\d\/]/g,'')"
        />
      </template>
    </date-picker>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'StepwiseDatePicker',
  components: {
    DatePicker
  },
  props: {
    value: String,
    step: {
      type: Number,
      default: 1,
      validator: value => [1, 2, 3].includes(value)
    },
    invalid: Boolean,
    placeholder: {
      type: String,
      default: 'mm/dd/yyyy'
    }
  },
  data() {
    return {
      internalStep: 1,
      internalYearDob: '',
      internalMonthDob: '',
      internalDob: this.value || '',
      yearPickerOpen: false,
      monthPickerOpen: false,
      dayPickerOpen: false,
      displayValue: this.value || '',
      isTyping: false
    };
  },
  computed: {
    defaultDateValue() {
      if (this.internalYearDob && this.internalMonthDob) {
        const year = parseInt(this.internalYearDob);
        const month = parseInt(this.internalMonthDob) - 1;
        return new Date(year, month, 1);
      }
      return null;
    }
  },
  watch: {
    value(newVal) {
      if (newVal !== this.internalDob && !this.isTyping) {
        this.internalDob = newVal || '';
        this.displayValue = newVal || '';
        if (newVal && this.isValidDate(newVal)) {
          this.parseDateToInternalValues(newVal);
        } else if (!newVal) {
          this.internalYearDob = '';
          this.internalMonthDob = '';
          this.internalDob = '';
          this.displayValue = '';
          this.internalStep = this.step;

          this.$nextTick(() => {
            this.resetAllPickers();
          });
        }
      }
    },
    internalDob(newVal) {
      if (newVal !== this.value) {
        this.$emit('input', newVal);
      }
    },
    step(newStep) {
      if (newStep >= 1 && newStep <= 3) {
        this.internalStep = newStep;
      }
    },
  },
  mounted() {
    // If we have an initial value, parse it
    if (this.value && this.isValidDate(this.value)) {
      this.parseDateToInternalValues(this.value);
      this.internalStep = 3;
    }
  },
  methods: {
    openYearPicker() {
      this.yearPickerOpen = true;
      this.internalStep = 1;
    },

    openMonthPicker() {
      this.monthPickerOpen = true;
      this.internalStep = 2;
    },

    openDayPicker() {
      this.dayPickerOpen = true;
      this.internalStep = 3;
    },

    handleYearSelect(value) {
      if (value) {
        this.yearPickerOpen = false;
        this.internalYearDob = value;
        setTimeout(() => {
          this.internalStep = 2;
          this.$nextTick(() => {
            this.monthPickerOpen = true;
          });
        }, 300);
      }
    },

    handleMonthSelect(value) {
      if (value) {
        this.internalMonthDob = value;
        this.monthPickerOpen = false;

        setTimeout(() => {
          this.internalStep = 3;
          this.$nextTick(() => {
            this.dayPickerOpen = true;
          });
        }, 300);
      }
    },

    handleDateSelect(value) {
      if (value) {
        if (!this.isDateWithinAgeRange(value)) {
          this.reset();
          return;
        }

        this.internalDob = value;
        this.displayValue = value;
        this.yearPickerOpen = false;
        this.monthPickerOpen = false;
        this.dayPickerOpen = false;
      }
    },

    // Manual input handlers
    handleManualYearInput(event) {
      this.isTyping = true;
      const value = event.target.value;
      this.displayValue = value;

      if (this.isValidDate(value)) {
        this.parseFullDate(value);
        this.internalStep = 3;
        this.yearPickerOpen = false;
        this.isTyping = false;
      } else if (value.length === 4 && /^\d{4}$/.test(value)) {
        this.internalYearDob = value || '';
        this.internalStep = 2;
        this.yearPickerOpen = false;
        this.$nextTick(() => {
          this.monthPickerOpen = true;
        });
        this.isTyping = false;
      } else if (this.isValidDateFormat(value) && !this.isDateWithinAgeRange(value)) {
        this.reset();
        this.internalStep = 1
        this.isTyping = false;
      }
    },

    handleManualMonthInput(event) {
      this.isTyping = true;
      const value = event.target.value;
      this.displayValue = value;

      if (this.isValidDate(value)) {
        this.parseFullDate(value);
        this.internalStep = 3;
        this.monthPickerOpen = false;
        this.isTyping = false;
      } else if (this.isValidDateFormat(value) && !this.isDateWithinAgeRange(value)) {
        this.reset();
        this.internalStep = 1
        this.isTyping = false;
      }
    },

    handleManualDateInput(event) {
      this.isTyping = true;
      let value = event?.target?.value;
      this.displayValue = value;

      if (!value) {
        this.internalStep = 1;
        this.internalDob = value;
        this.yearPickerOpen = false;
        this.monthPickerOpen = false;
        this.dayPickerOpen = false;
        this.isTyping = false;
        this.reset();
        this.isTyping = false;
      } else if (this.isValidDateFormat(value)) {
        if (this.isDateWithinAgeRange(value)) {
          // Valid date within age range
          this.parseFullDate(value);
          this.internalStep = 3;
          this.dayPickerOpen = false;
          this.isTyping = false;
        }
      }
    },

    // Formatting methods
    formatYearInput(event) {
      const input = event.target;
      let value = input.value.replace(/[^\d/]/g, '');

      if (value.length >= 2 && !value.includes('/')) {
        value = value.substr(0, 2) + '/' + value.substr(2);
      }
      if (value.length >= 5 && value.split('/').length < 3) {
        value = value.substr(0, 5) + '/' + value.substr(5);
      }
      if (value.length > 10) value = value.substr(0, 10);

      input.value = value;
      this.displayValue = value;
    },

    formatMonthInput(event) {
      this.formatDateInput(event);
    },

    formatDateInput(event) {
      const input = event.target;
      let value = input.value.replace(/[^\d]/g, '');

      if (value.length > 8) value = value.substr(0, 8);
      if (value.length >= 2) value = value.substr(0, 2) + '/' + value.substr(2);
      if (value.length >= 5) value = value.substr(0, 5) + '/' + value.substr(5);

      input.value = value;
      this.displayValue = value;
    },

    // Validation and parsing methods
    isValidDate(dateString) {
      if (!this.isValidDateFormat(dateString)) return false;

      const parts = dateString.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;

      const date = new Date(year, month - 1, day);
      const isValid = date.getMonth() === month - 1 &&
          date.getDate() === day &&
          date.getFullYear() === year;

      if (!isValid) return false;
      return this.isDateWithinAgeRange(dateString);
    },

    parseFullDate(dateString) {
      if (!dateString || !this.isValidDate(dateString)) return;

      const parts = dateString.split('/');
      this.internalMonthDob = parts[0];
      this.internalYearDob = parts[2];
      this.internalDob = dateString;
      this.displayValue = dateString;

      this.$emit('input', dateString);
      this.$emit('change', dateString);
    },

    parseDateToInternalValues(dateString) {
      if (!dateString || !this.isValidDate(dateString)) return;

      const parts = dateString.split('/');
      this.internalMonthDob = parts[0];
      this.internalYearDob = parts[2];
    },

    reset() {
      this.internalStep = 1;
      this.internalYearDob = '';
      this.internalMonthDob = '';
      this.internalDob = '';
      this.displayValue = '';
      this.yearPickerOpen = false;
      this.monthPickerOpen = false;
      this.dayPickerOpen = false;
      this.$nextTick(() => {
        // Destroying the library catch and restarting the component
        if (this.$refs.yearPicker) {
          const yearEl = this.$refs.yearPicker.$el;
          if (yearEl.parentNode) {
            const parent = yearEl.parentNode;
            const nextSibling = yearEl.nextSibling;
            parent.removeChild(yearEl);
            setTimeout(() => {
              if (nextSibling) {
                parent.insertBefore(yearEl, nextSibling);
              } else {
                parent.appendChild(yearEl);
              }
            }, 100);
          }
        }
      });
    },

    resetAllPickers() {
      ['yearPicker', 'monthPicker', 'dayPicker'].forEach(picker => {
        this.resetDomElement(picker);
      });
    },

    resetDomElement(refName) {
      if (!this.$refs[refName]) return;

      const element = this.$refs[refName].$el;
      if (!element.parentNode) return;

      const parent = element.parentNode;
      const nextSibling = element.nextSibling;

      // Remove from DOM
      parent.removeChild(element);

      // Add back
      this.$nextTick(() => {
        if (nextSibling) {
          parent.insertBefore(element, nextSibling);
        } else {
          parent.appendChild(element);
        }
      });
      this.internalStep = 1
    },

    isDateWithinAgeRange(dateString) {
      if (!dateString || !this.isValidDateFormat(dateString)) return false;

      const parts = dateString.split('/');
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      const date = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const minDate = new Date(today);
      minDate.setFullYear(today.getFullYear() - 100);
      minDate.setHours(0, 0, 0, 0);

      const maxDate = today;

      return date >= minDate && date <= maxDate;
    },

    isValidDateFormat(dateString) {
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(dateString);
    },

    handleClear() {
      this.isTyping = true;
      this.internalDob = '';
      this.displayValue = '';
      this.internalStep = 1;
      this.yearPickerOpen = false;
      this.monthPickerOpen = false;
      this.dayPickerOpen = false;
      this.isTyping = false;
      this.$nextTick(() => {
        this.resetAllPickers();
      });
      this.reset();
    },
  }
};
</script>

<style scoped>
.stepwise-date-picker {
  width: 100%;
}

.custom-input {
  width: 100%;
  border: 1px solid #dddddd;
  border-radius: 6px;
  font-size: 14px;
  padding: 10px 15px;
  outline: none;
  font-weight: 500;
  background-color: white;
  cursor: pointer;
}

.custom-input.invalid {
  border-color: #dc3545;
}
</style>




